import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import type {
  LeaderboardEntryDto,
  LeaderboardMetricDto,
  LeaderboardResponse,
  LeaderboardType,
} from '@rex-mortis/shared';
import { titleChipHtml } from '../components/Title.js';

interface FlashState {
  kind: 'success' | 'danger';
  message: string;
}

interface State {
  type: LeaderboardType;
  metric: string;
  metricsForType: LeaderboardMetricDto[];
  data: LeaderboardResponse | null;
  page: number;
  flash: FlashState | null;
}

const PAGE_SIZE = 25;

export async function renderLeaderboards(root: HTMLElement): Promise<void> {
  const state: State = {
    type: 'players',
    metric: 'level',
    metricsForType: [],
    data: null,
    page: 0,
    flash: null,
  };

  let metricsCache: { players: LeaderboardMetricDto[]; houses: LeaderboardMetricDto[] } | null = null;

  async function ensureMetrics(): Promise<void> {
    if (metricsCache) return;
    try {
      metricsCache = await api.leaderboardMetrics();
    } catch (err) {
      state.flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed to load metric list.' };
    }
  }

  async function refresh(): Promise<void> {
    try {
      const r = await api.getLeaderboard(state.type, state.metric, {
        limit: PAGE_SIZE,
        offset: state.page * PAGE_SIZE,
      });
      state.data = r;
    } catch (err) {
      state.data = null;
      state.flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed to load leaderboard.' };
    }
  }

  await ensureMetrics();
  state.metricsForType = metricsCache?.[state.type] ?? [];
  await refresh();

  mountShell(root, (main) => {
    const renderMain = (): void => {
      const totalPages = state.data ? Math.max(1, Math.ceil(state.data.total / PAGE_SIZE)) : 1;

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/leaderboards.webp" alt="" />
        <h1>The Annals</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          Names recorded in the scribes' ledger — those who have climbed highest.
        </p>

        <div id="flash"></div>

        <div class="panel">
          <div class="lb-tabs">
            <button class="lb-tab ${state.type === 'players' ? 'active' : ''}" data-type="players">Players</button>
            <button class="lb-tab ${state.type === 'houses' ? 'active' : ''}" data-type="houses">Houses</button>
          </div>
          <div class="lb-metric-row">
            <label for="lb-metric">Rank by</label>
            <select id="lb-metric">
              ${state.metricsForType.map((m) => `
                <option value="${m.slug}" ${m.slug === state.metric ? 'selected' : ''}>${escapeHtml(m.label)}</option>
              `).join('')}
            </select>
          </div>
        </div>

        <div class="panel">
          ${
            state.data && state.data.entries.length > 0
              ? renderTable(state.data, state.metric)
              : '<p style="font-style:italic; color: var(--c-ink-soft);">No entries yet.</p>'
          }
          ${
            state.data
              ? `<div class="lb-pager">
                   <button class="btn ghost" id="lb-prev" ${state.page === 0 ? 'disabled' : ''}>← Prev</button>
                   <span class="lb-page-info">Page ${state.page + 1} of ${totalPages} · ${state.data.total} total</span>
                   <button class="btn ghost" id="lb-next" ${state.page + 1 >= totalPages ? 'disabled' : ''}>Next →</button>
                 </div>`
              : ''
          }
        </div>
      `;

      const style = document.createElement('style');
      style.textContent = LB_STYLES;
      main.appendChild(style);

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (state.flash) {
        const div = document.createElement('div');
        div.className = `alert ${state.flash.kind}`;
        div.textContent = state.flash.message;
        flashRoot.appendChild(div);
      }

      // Tab toggle.
      main.querySelectorAll<HTMLButtonElement>('button.lb-tab').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const next = btn.dataset.type as LeaderboardType;
          if (next === state.type) return;
          state.type = next;
          state.page = 0;
          state.metricsForType = metricsCache?.[next] ?? [];
          // Default metric for each type.
          state.metric = next === 'players' ? 'level' : 'renown';
          await refresh();
          anchored(renderMain);
        });
      });

      // Metric picker.
      main.querySelector<HTMLSelectElement>('#lb-metric')?.addEventListener('change', async (e) => {
        const next = (e.target as HTMLSelectElement).value;
        if (next === state.metric) return;
        state.metric = next;
        state.page = 0;
        await refresh();
        anchored(renderMain);
      });

      // Pagination.
      main.querySelector<HTMLButtonElement>('#lb-prev')?.addEventListener('click', async () => {
        if (state.page > 0) {
          state.page--;
          await refresh();
          anchored(renderMain);
        }
      });
      main.querySelector<HTMLButtonElement>('#lb-next')?.addEventListener('click', async () => {
        state.page++;
        await refresh();
        anchored(renderMain);
      });
    };

    anchored(renderMain);
  });
}

function renderTable(d: LeaderboardResponse, _metric: string): string {
  return `
    <div class="table-scroll">
    <table class="lb-table">
      <thead>
        <tr>
          <th class="lb-col-rank">#</th>
          <th class="lb-col-name">${d.type === 'houses' ? 'House' : 'Player'}</th>
          <th class="lb-col-value">${escapeHtml(d.metricLabel)}</th>
          <th class="lb-col-meta"></th>
        </tr>
      </thead>
      <tbody>
        ${d.entries.map(renderRow).join('')}
      </tbody>
    </table>
    </div>
  `;
}

function renderRow(e: LeaderboardEntryDto): string {
  const rankClass = e.rank === 1 ? 'rank-1' : e.rank === 2 ? 'rank-2' : e.rank === 3 ? 'rank-3' : '';
  // Player rows link to the public profile; House rows (no characterId) stay plain.
  // Active members get their flair colour on the name.
  const flair = e.membershipFlairColour ?? null;
  const flairStyle = flair
    ? ` style="color:${flair}; text-shadow:-1px -1px 0 #000,1px -1px 0 #000,-1px 1px 0 #000,1px 1px 0 #000;"`
    : '';
  // House rows show a small banner thumbnail before the name.
  const bannerThumb = e.houseId
    ? `<img class="lb-banner-thumb" src="${escapeHtml(e.bannerUrl || '/img/banners/house-default.webp')}" alt="" onerror="this.onerror=null;this.src='/img/banners/houses.webp'" />`
    : '';
  const nameHtml = e.characterId
    ? `<a class="lb-name-link" href="#/u/${encodeURIComponent(e.characterId)}"><strong${flairStyle}>${escapeHtml(e.name)}</strong></a>`
    : e.houseId
      ? `${bannerThumb}<a class="lb-name-link" href="#/house/${encodeURIComponent(e.houseId)}"><strong>${escapeHtml(e.name)}</strong></a>`
      : `<strong>${escapeHtml(e.name)}</strong>`;
  return `
    <tr class="lb-row ${rankClass}">
      <td class="lb-col-rank">${e.rank}</td>
      <td class="lb-col-name">${nameHtml}${titleChipHtml(e.activeTitleSlug ?? null)}</td>
      <td class="lb-col-value">${formatBigDigits(e.value)}</td>
      <td class="lb-col-meta">${e.meta ? escapeHtml(e.meta) : ''}</td>
    </tr>
  `;
}

function formatBigDigits(s: string): string {
  // Treat as a (possibly very large) integer string and add thousands separators.
  // If it contains a decimal point, separate only the integer part.
  const dot = s.indexOf('.');
  const intPart = dot >= 0 ? s.slice(0, dot) : s;
  const fracPart = dot >= 0 ? s.slice(dot) : '';
  const negative = intPart.startsWith('-');
  const raw = negative ? intPart.slice(1) : intPart;
  const parts: string[] = [];
  for (let i = raw.length; i > 0; i -= 3) {
    parts.unshift(raw.slice(Math.max(0, i - 3), i));
  }
  return (negative ? '-' : '') + parts.join(',') + fracPart;
}

const LB_STYLES = `
  .lb-tabs {
    display: flex;
    gap: var(--s-2);
    margin-bottom: var(--s-3);
  }
  .lb-tab {
    flex: 0 0 auto;
    font-family: var(--font-display);
    font-size: 1rem;
    letter-spacing: 0.04em;
    padding: 8px 18px;
    background: rgba(255, 245, 220, 0.55);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    /* The inactive tab's cream cell is hardcoded (doesn't flip in dark mode), so
       pin its text dark in BOTH themes — var(--c-ink-soft) flips to cream and
       turns cream-on-cream. */
    color: rgba(28, 17, 8, 0.72);
    cursor: pointer;
  }
  .lb-tab.active {
    background: var(--c-oxblood);
    color: #fff5e1;
    border-color: var(--c-oxblood-deep);
  }
  .lb-metric-row {
    display: flex;
    align-items: center;
    gap: var(--s-3);
  }
  .lb-metric-row label {
    font-family: var(--font-display);
    color: var(--c-ink-soft);
    letter-spacing: 0.04em;
  }
  .lb-metric-row select {
    font: inherit;
    font-family: var(--font-body);
    padding: 6px 10px;
    /* Colours come from the global pinned cream cell in styles/pages.css. */
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    box-shadow: var(--shadow-press);
  }
  .lb-table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--font-body);
  }
  .lb-table th,
  .lb-table td {
    text-align: left;
    padding: 8px 12px;
    border-bottom: 1px solid var(--c-rule-soft);
  }
  .lb-table th {
    font-family: var(--font-display);
    color: var(--c-ink-soft);
    font-size: 0.92rem;
    letter-spacing: 0.04em;
    background: rgba(214, 195, 147, 0.30);
  }
  .lb-col-rank { width: 60px; font-family: var(--font-numeric); font-weight: 700; color: var(--c-ink-soft); }
  .lb-col-value { font-family: var(--font-numeric); font-weight: 700; text-align: right; color: var(--c-oxblood); }
  .lb-col-meta { font-family: var(--font-display); color: var(--c-ink-soft); font-size: 0.95rem; text-align: right; }
  .lb-row.rank-1 .lb-col-rank,
  .lb-row.rank-1 .lb-col-name strong { color: var(--c-gold-text); }
  .lb-row.rank-2 .lb-col-rank,
  .lb-row.rank-2 .lb-col-name strong { color: #a8a8a8; }
  .lb-row.rank-3 .lb-col-rank,
  .lb-row.rank-3 .lb-col-name strong { color: #b07238; }
  .lb-row.rank-1 .lb-col-rank::before { content: '♛ '; }
  .lb-row.rank-2 .lb-col-rank::before { content: '✦ '; }
  .lb-row.rank-3 .lb-col-rank::before { content: '✧ '; }
  .lb-pager {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: var(--s-4);
    gap: var(--s-3);
    font-family: var(--font-display);
    color: var(--c-ink-soft);
  }
  .lb-page-info { font-size: 0.92rem; }
`;
